import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';

import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email.'),
  password: z.string().min(1, 'Password is required.'),
});

type LoginValues = z.infer<typeof loginSchema>;

export function Login() {
  const { signIn, error, clearError, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: LoginValues) => {
    setSubmitError(null);
    try {
      const user = await signIn(values.email, values.password);
      const next = params.get('next');
      redirectByRole(user, navigate, next);
    } catch (e) {
      const m = (e as { message?: string })?.message || 'Login failed.';
      setSubmitError(m);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            Authenticate with your agency administrator credentials.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {(submitError ?? error) && (
              <p className="text-sm text-destructive">{submitError ?? error}</p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
            <Link to="/register-agency" className="text-sm text-muted-foreground hover:underline" onClick={clearError}>
              Don't have an agency yet? Register →
            </Link>
          </CardFooter>
        </form>
      </Card>
    </main>
  );
}

function redirectByRole(
  user: { role: string },
  navigate: ReturnType<typeof useNavigate>,
  next: string | null,
) {
  if (next) {
    navigate(next, { replace: true });
    return;
  }
  const path =
    user.role === 'super_admin' ? '/admin/tenants' :
    user.role === 'client' ? '/me' :
    '/dashboard';
  navigate(path, { replace: true });
}